import ViewFilter from "@/domain/entities/customViews/ViewFilter";
import AISuggestion from "@/domain/enums/AISuggestions";
import useGenerateViewFilters from "@/hooks/customView/useGenerateViewFilters";
import useGetView from "@/hooks/customView/useGetView";
import useGetTokenProfile from "@/hooks/useGetTokenProfile";
import { cn } from "@/lib/utils";
import { FormEvent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import InitialInput from "./InitialInput";
import ViewConfig from "./ViewConfig";

interface CustomViewDialogProps {
    trigger: ReactNode;
    viewId?: string;
}

const CustomViewDialog = ({ trigger, viewId }: CustomViewDialogProps) => {
    const { t } = useTranslation();
    const { data: tokenProfile } = useGetTokenProfile();
    const isCreationBlocked = !viewId && (tokenProfile?.isExhausted ?? false);
    const [inputValue, setInputValue] = useState<string>("");
    const [prompt, setPrompt] = useState<string>("");
    const [isOpen, setIsOpen] = useState(false);
    const [AIFilters, setAIFilters] = useState<ViewFilter>();
    const [isLoadingAIFilters, setIsLoadingAIFilters] = useState(false);
    const { data: view } = useGetView(viewId);
    const generateViewFilters = useGenerateViewFilters();

    const handleSubmitInput = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!inputValue) return;

        setIsLoadingAIFilters(true);
        const AIFiltersData = await generateViewFilters(inputValue);
        setAIFilters(AIFiltersData);
        setIsLoadingAIFilters(false);
        setPrompt(inputValue);
    };

    const handleOnOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (!open) {
            setInputValue("");
            setPrompt("");
            setAIFilters(undefined);
            setIsLoadingAIFilters(false);
        }
    };

    const renderTrigger = () => {
        if (isCreationBlocked) {
            return (
                <TooltipProvider delayDuration={100}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                className={cn(
                                    "inline-flex cursor-not-allowed opacity-50",
                                    "[&_svg]:pointer-events-none [&_svg]:text-text-caption",
                                )}
                                aria-disabled
                            >
                                {trigger}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {t("navbar.customViews.creationDisabledTokens")}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            );
        }

        return <DialogTrigger>{trigger}</DialogTrigger>;
    };

    return (
        <Dialog onOpenChange={handleOnOpenChange} open={isOpen}>
            {renderTrigger()}
            <DialogContent className="p-0">
                <DialogHeader className="p-10 pb-0">
                    <DialogTitle>
                        {viewId
                            ? t("customViewDialog.title.edit", {
                                  viewName: view?.name,
                              })
                            : t("customViewDialog.title.create")}
                    </DialogTitle>
                </DialogHeader>
                {!prompt && !view && isCreationBlocked && (
                    <p className="p-10 pt-5 text-sm text-destructive">
                        {t("navbar.customViews.creationDisabledTokens")}
                    </p>
                )}
                {!prompt && !view && !isCreationBlocked && (
                    <InitialInput
                        className="p-10 pt-5"
                        handleSubmitInput={handleSubmitInput}
                        inputValue={inputValue}
                        isSendingMessage={isLoadingAIFilters}
                        placeholder={t(
                            "customViewDialog.vistaInput.placeholder",
                        )}
                        setInputValue={setInputValue}
                        suggestionType={AISuggestion.CustomViewCreation}
                    />
                )}
                {(prompt || view) && (
                    <ViewConfig
                        AIAnalysis={AIFilters}
                        prompt={prompt}
                        setInputValue={setInputValue}
                        setIsOpen={setIsOpen}
                        setPrompt={setPrompt}
                        view={view || undefined}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
export default CustomViewDialog;
